<script setup lang="ts">
import { computed } from 'vue'

/**
 * The five states, drawn the same way on every page so a status reads by its
 * shape before its colour.
 */
export type StatusGlyphKind = 'live' | 'ready' | 'needs' | 'holding' | 'off'

const props = withDefaults(defineProps<{ kind: StatusGlyphKind; size?: number }>(), { size: 14 })
const glyph = computed(() => {
  switch (props.kind) {
    case 'live':
      return { ring: 'var(--studio-live-ring)', dot: true }
    case 'ready':
      return { ring: 'var(--studio-ready)', mark: 'M5.4 8.2l1.8 1.8 3.4-3.7', markColor: 'var(--studio-ready)' }
    case 'needs':
      return { ring: 'var(--studio-needs)', fill: 'var(--studio-needs)', mark: 'M8 4.7v3.8M8 11.1v.1', markColor: 'var(--studio-ground)' }
    case 'holding':
      return { ring: 'var(--studio-border-strong)', arc: true }
    default:
      return { ring: 'var(--studio-subtle)', dash: '1.8 2.2' }
  }
})
</script>

<template>
  <svg class="status-glyph" :data-kind="kind" viewBox="0 0 16 16" :width="size" :height="size" aria-hidden="true" focusable="false">
    <circle cx="8" cy="8" r="6.25" :fill="glyph.fill ?? 'none'" :stroke="glyph.ring" stroke-width="1.5" :stroke-dasharray="glyph.dash" />
    <path
      v-if="glyph.arc"
      d="M8 1.75a6.25 6.25 0 0 1 6.25 6.25"
      fill="none"
      stroke="var(--studio-accent)"
      stroke-width="1.5"
      stroke-linecap="round"
    />
    <circle v-if="glyph.dot" cx="8" cy="8" r="3.5" fill="var(--studio-live)" />
    <path
      v-if="glyph.mark"
      :d="glyph.mark"
      fill="none"
      :stroke="glyph.markColor"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
</template>

<style scoped>
.status-glyph {
  flex: none;
  display: block;
}
.status-glyph[data-kind='holding'] path {
  transform-origin: 8px 8px;
  animation: status-glyph-turn 1.2s linear infinite;
}
@keyframes status-glyph-turn {
  to {
    transform: rotate(360deg);
  }
}
@media (prefers-reduced-motion: reduce) {
  .status-glyph[data-kind='holding'] path {
    animation: none;
  }
}
</style>
